import { Divider } from "../components";
import { DataTable } from "../components";
import appStyles from "../Overview.module.css";
import type {
  JobTypeUsage,
  ResourceDetail,
  ResponseData,
  Usage,
} from "../types";
import {
  GetGraphDashboardAccount,
  GetGraphDashboardAccountRow,
  GetGraphDashboardOrganization,
  GetGraphDashboardStorage,
  GetGraphDashboardUser,
} from "./graphs";
import styles from "./OverviewTable.module.css";
import {
  StatsSectionBiggestJobs,
  StatsSectionHardware,
  StatsSectionUsage,
  StatsSectionUsageTotal,
} from "./StatsSection";
import statStyles from "./StatsSection.module.css";

interface OverviewTableProps {
  responseData: ResponseData;
}

export default function OverviewTable({ responseData }: OverviewTableProps) {
  const [pStats, NPStats] = [
    responseData.job_types.P,
    responseData.job_types.NP,
  ].map((data, i): JobTypeUsage => {
    const usage: Usage = {
      total: { ...data.resources.quota, rtype: "Total" },
      users: Object.entries(data.resources.inuse || {})
        .sort()
        .map(([user, quants]) => ({ user, ...quants })),
      npUsed: null,
      used: null,
      free: null,
    };

    // NP usage is only in the Preemptable table.
    if (i === 0) {
      usage.npUsed = {
        njobs: data.resources.np_inuse_total?.njobs || 0,
        cpu: data.resources.np_inuse_total?.cpu || 0,
        gpu: data.resources.np_inuse_total?.gpu || 0,
        mem: data.resources.np_inuse_total?.mem || 0,
        ibd: data.resources.np_inuse_total?.ibd || 0,
        rtype: "NP Used",
      };
    } else {
      usage.npUsed = null;
    }

    usage.used = Object.values(data.resources.inuse || {}).reduce(
      (acc, cur) => {
        acc.njobs = (acc.njobs || 0) + (cur.njobs || 0);
        acc.cpu = (acc.cpu || 0) + (cur.cpu || 0);
        acc.gpu = (acc.gpu || 0) + (cur.gpu || 0);
        acc.mem = (acc.mem || 0) + (cur.mem || 0);
        return acc;
      },
      {
        njobs: 0,
        cpu: 0,
        gpu: 0,
        mem: 0,
        rtype: "Used",
      },
    );

    usage.free = {
      cpu:
        (usage.total?.cpu || 0) -
        (data.resources.np_inuse_total?.cpu || 0) -
        (usage.used?.cpu || 0),
      gpu:
        (usage.total?.gpu || 0) -
        (data.resources.np_inuse_total?.gpu || 0) -
        (usage.used?.gpu || 0),
      mem:
        (usage.total?.mem || 0) -
        (data.resources.np_inuse_total?.mem || 0) -
        (usage.used?.mem || 0),
      njobs: undefined,
      rtype: "Available",
    };
    return {
      resources: usage,
      models: {
        cpu: Object.fromEntries(
          Object.entries(data.models?.cpu || {}).map(([name, stats]) => [
            name,
            { ...stats },
          ]),
        ),
        gpu: Object.fromEntries(
          Object.entries(data.models?.gpu || {}).map(([name, stats]) => [
            name,
            { ...stats },
          ]),
        ),
      },
      biggest_jobs: data.biggest_jobs,
    };
  });

  const [myPStats, myNPStats] = [
    responseData.job_types.P,
    responseData.job_types.NP,
  ].map((data, _) => {
    const {
      resources: { inuse = {} },
    } = data;

    const usage = {
      users: Object.entries(inuse)
        .sort()
        .map(([user, quants]) => ({ user, ...quants }))
        .filter(function (user) {
          return user.user === responseData.me.account;
        }),
    };
    return {
      resources: usage,
    };
  });
  const showMyAccount =
    myPStats.resources.users.length + myNPStats.resources.users.length > 0;

  const prioritizedAccountsConf = {
    title: "Priorities",
    headers: ["Account", "Occupancy", "Resources"],
    cols: [
      {
        key: "name",
        url: GetGraphDashboardAccountRow.bind(null, responseData.graphURL),
      },
      { key: "occupancy", transform: (val: number) => val && val.toFixed(2) },
      {
        key: "resources",
        component: ({
          compKey,
          className,
          value,
        }: {
          compKey: string;
          className: string;
          value: ResourceDetail[];
        }) => (
          <td className={className}>
            {value.map((req, index) => (
              <div key={compKey + index}>{`${req.resource} ${
                req.preemptable ? "P" : "NP"
              } (${req.jobs})`}</div>
            ))}
          </td>
        ),
      },
    ],
    data: responseData.prioritized_accounts.sort(
      (a, b) => Number(b.user) - Number(a.user),
    ),
  };

  return (
    <>
      <div className={styles.dashboard}>
        <h3 className={styles.subTitle}>Dashboards</h3>
        <p>
          You have also access to dashboards providing insightful information on
          resource utilization/waste to help you optimize your workloads:
        </p>
        <ul>
          <li>
            <a
              href={GetGraphDashboardUser(
                responseData.graphURL,
                responseData.me.id,
              )}
              target="_blank"
              rel="noreferrer"
              title={`Dashboard for the user: ${
                responseData.me.fullName ||
                responseData.me.mail ||
                responseData.me.id
              }`}
            >
              My user dashboard
            </a>
          </li>
          <li>
            <a
              href={GetGraphDashboardAccount(
                responseData.graphURL,
                responseData.me.account,
              )}
              target="_blank"
              rel="noreferrer"
              title={`Dashboard for the account: ${responseData.me.account}`}
            >
              My account dashboard
            </a>
          </li>
          <li>
            <a
              href={GetGraphDashboardOrganization(
                responseData.graphURL,
                responseData.org.id,
              )}
              target="_blank"
              rel="noreferrer"
              title={`Dashboard for the organization: ${
                responseData.org.fullName || responseData.org.id
              }`}
            >
              Organization dashboard
            </a>
          </li>
          <li>
            <a
              href={GetGraphDashboardStorage(
                responseData.graphURL,
                responseData.org.id,
              )}
              target="_blank"
              rel="noreferrer"
              title={`Dashboard for storage: ${
                responseData.org.fullName || responseData.org.id
              }`}
            >
              Storage dashboard
            </a>
          </li>
        </ul>
      </div>

      {showMyAccount && (
        <div className={styles.jobs}>
          <div className={styles.jobSection}>
            <StatsSectionUsage
              title="My Account"
              stats={{
                pStats: { data: myPStats, name: "Preemptable" },
                NPStats: { data: myNPStats, name: "Non Preemptable" },
              }}
              graphURL={responseData.graphURL}
            />
          </div>
        </div>
      )}

      <div className={appStyles.vspacer} />

      <div className={styles.jobs}>
        <div className={styles.jobSection}>
          <StatsSectionUsageTotal
            stats={{
              pStats: { data: pStats, name: "Preemptable" },
              NPStats: { data: NPStats, name: "Non Preemptable" },
            }}
          />

          <div className={appStyles.vspacer} />

          <StatsSectionUsage
            stats={{
              pStats: { data: pStats, name: "Preemptable" },
              NPStats: { data: NPStats, name: "Non Preemptable" },
            }}
            graphURL={responseData.graphURL}
          />

          <div className={appStyles.vspacer} />

          <StatsSectionHardware
            stats={{
              pStats: { data: pStats, name: "Preemptable" },
              NPStats: { data: NPStats, name: "Non Preemptable" },
            }}
          />

          <div className={appStyles.vspacer} />

          <StatsSectionBiggestJobs
            stats={{
              pStats: { data: pStats, name: "Preemptable" },
              NPStats: { data: NPStats, name: "Non Preemptable" },
            }}
          />
        </div>
      </div>

      <div className={appStyles.vspacer} />
      <div className={appStyles.vspacer} />
      <h3 className={appStyles.title}>Accounts to be Scheduled Next</h3>
      <Divider />
      <div
        className={statStyles.tableContainer}
        style={{ marginTop: ".675em" }}
      >
        <DataTable {...prioritizedAccountsConf} />
      </div>
    </>
  );
}
